import { useState, useRef, useCallback, useEffect } from 'react';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cfImageUrl } from '@viet-parenting/supabase/cf-images';
import { supabase } from '../lib/supabase';

const IMAGES_HASH = import.meta.env.VITE_CF_IMAGES_ACCOUNT_HASH;

interface PhotoState {
  key: string;
  previewUrl: string;
  id: string | null;
  uploading: boolean;
  error: boolean;
}

interface Props {
  value: string[];
  onChange: (ids: string[]) => void;
  maxFiles?: number;
}

export default function PhotoUploader({ value, onChange, maxFiles = 10 }: Props) {
  const [photos, setPhotos] = useState<PhotoState[]>(() =>
    value.map((id) => ({
      key: id,
      previewUrl: cfImageUrl(id, IMAGES_HASH, 'thumbnail'),
      id,
      uploading: false,
      error: false,
    })),
  );
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync upward whenever the set or order of completed IDs changes
  useEffect(() => {
    const ids = photos.filter((p) => p.id && !p.error).map((p) => p.id as string);
    if (ids.length !== value.length || ids.some((id, i) => id !== value[i])) {
      onChange(ids);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos]);

  const uploadFile = useCallback(async (key: string, file: File) => {
    try {
      const { data, error } = await supabase.functions.invoke<{
        id: string;
        uploadURL?: string;
      }>('cf-image-upload');
      if (error || !data) throw error ?? new Error('upload-url failed');

      if (data.uploadURL) {
        const fd = new FormData();
        fd.append('file', file);
        const cfRes = await fetch(data.uploadURL, { method: 'POST', body: fd });
        if (!cfRes.ok) throw new Error(`cf upload ${cfRes.status}`);
      }

      setPhotos((prev) =>
        prev.map((p) => (p.key === key ? { ...p, id: data.id, uploading: false } : p)),
      );
    } catch {
      setPhotos((prev) => prev.map((p) => (p.key === key ? { ...p, uploading: false, error: true } : p)));
    }
  }, []);

  const handleFiles = useCallback(
    (files: FileList) => {
      const remaining = maxFiles - photos.length;
      if (remaining <= 0) return;

      const incoming = Array.from(files).slice(0, remaining);
      const next: PhotoState[] = incoming.map((file) => ({
        key: `${Date.now()}-${Math.random()}`,
        previewUrl: URL.createObjectURL(file),
        id: null,
        uploading: true,
        error: false,
      }));

      setPhotos((prev) => [...prev, ...next]);
      incoming.forEach((file, i) => {
        const item = next[i];
        if (item) void uploadFile(item.key, file);
      });
    },
    [photos.length, maxFiles, uploadFile],
  );

  const remove = useCallback((key: string) => {
    setPhotos((prev) => {
      const photo = prev.find((p) => p.key === key);
      if (photo && photo.previewUrl.startsWith('blob:')) URL.revokeObjectURL(photo.previewUrl);
      return prev.filter((p) => p.key !== key);
    });
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setPhotos((prev) => {
      const oldIndex = prev.findIndex((p) => p.key === active.id);
      const newIndex = prev.findIndex((p) => p.key === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  return (
    <div>
      {photos.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={photos.map((p) => p.key)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-3">
              {photos.map((photo, idx) => (
                <SortablePhoto
                  key={photo.key}
                  photo={photo}
                  isCover={idx === 0}
                  onRemove={remove}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {photos.length < maxFiles && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full border-2 border-dashed border-gray-200 rounded-lg py-6 text-sm text-gray-400 hover:border-red-300 hover:text-red-500 transition-colors flex flex-col items-center gap-1"
        >
          <span className="text-2xl">📷</span>
          <span>{photos.length === 0 ? 'Thêm ảnh' : 'Thêm ảnh khác'}</span>
          {maxFiles > 1 && <span className="text-xs">JPG, PNG, WebP · tối đa {maxFiles} ảnh</span>}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple={maxFiles > 1}
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) {
            handleFiles(e.target.files);
            e.target.value = '';
          }
        }}
      />
    </div>
  );
}

interface SortablePhotoProps {
  photo: PhotoState;
  isCover: boolean;
  onRemove: (key: string) => void;
}

function SortablePhoto({ photo, isCover, onRemove }: SortablePhotoProps) {
  const dragDisabled = photo.uploading || photo.error;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: photo.key,
    disabled: dragDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group aspect-square ${dragDisabled ? '' : 'cursor-grab active:cursor-grabbing'} ${
        isDragging ? 'shadow-lg scale-[1.02]' : ''
      }`}
      {...attributes}
      {...listeners}
    >
      <img
        src={photo.previewUrl}
        alt=""
        draggable={false}
        className="w-full h-full object-cover rounded-lg border border-gray-200 select-none pointer-events-none"
      />

      {isCover && !photo.uploading && !photo.error && (
        <span className="absolute top-1 left-1 text-xs bg-red-700 text-white px-1.5 py-0.5 rounded font-medium leading-none pointer-events-none">
          Cover
        </span>
      )}

      {photo.uploading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/75 rounded-lg pointer-events-none">
          <div className="w-5 h-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {photo.error && (
        <div className="absolute inset-0 flex items-center justify-center bg-red-50/90 rounded-lg pointer-events-none">
          <span className="text-xs text-red-600 font-medium">Lỗi</span>
        </div>
      )}

      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(photo.key);
        }}
        className="absolute top-1 right-1 w-5 h-5 bg-gray-900/60 hover:bg-gray-900/80 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20"
        aria-label="Xóa ảnh"
      >
        ×
      </button>
    </div>
  );
}
